import React, { useState, useEffect } from 'react';
import { useCall } from '../contexts/CallContext';
import { Phone, Mic, MicOff, Volume2, VolumeX, Grid3x3, Minimize2 } from 'lucide-react';
import { createPortal } from 'react-dom';

export default function ActiveCallModal() {
  const { activeCallState, endCall, toggleMute, toggleSpeaker, sendDTMF, minimizeCall } = useCall();
  const [showKeypad, setShowKeypad] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  useEffect(() => {
    if (activeCallState.callStatus === 'connected' && activeCallState.callStartTime) {
      const interval = setInterval(() => {
        const now = new Date();
        const start = activeCallState.callStartTime!;
        const duration = Math.floor((now.getTime() - start.getTime()) / 1000);
        setCallDuration(duration);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setCallDuration(0);
    }
  }, [activeCallState.callStatus, activeCallState.callStartTime]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const nationalNumber = cleaned.startsWith('1') ? cleaned.substring(1) : cleaned;
    if (nationalNumber.length === 10) {
      return `(${nationalNumber.substring(0, 3)}) ${nationalNumber.substring(3, 6)}-${nationalNumber.substring(6, 10)}`;
    }
    return phoneNumber;
  };

  const handleKeypadPress = (digit: string) => {
    sendDTMF(digit);
  };

  if (activeCallState.callStatus === 'idle' || activeCallState.isMinimized) {
    return null;
  }

  const displayName = activeCallState.customerInfo
    ? `${activeCallState.customerInfo.first_name || ''} ${activeCallState.customerInfo.last_name || ''}`.trim()
    : formatPhoneNumber(activeCallState.phoneNumber);

  const callStatusText =
    activeCallState.callStatus === 'connecting' ? 'Connecting...' :
    activeCallState.callStatus === 'ringing' ? 'Ringing...' :
    activeCallState.callStatus === 'connected' ? formatDuration(callDuration) :
    '';

  return createPortal(
    <div
      className="fixed inset-0 z-[100050] flex flex-col"
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      }}
    >
      {/* Top safe area + minimize button */}
      <div
        className="flex items-center justify-between px-5"
        style={{
          paddingTop: 'max(1rem, env(safe-area-inset-top, 1rem))',
        }}
      >
        <div className="w-12" />
        <p className="text-white/60 text-xs font-medium tracking-widest uppercase">
          {activeCallState.callStatus === 'connected' ? 'On Call' : 'Calling'}
        </p>
        <button
          onClick={minimizeCall}
          className="w-12 h-12 flex items-center justify-center text-white/70 hover:text-white rounded-full bg-white/10 hover:bg-white/20 transition-all active:scale-95 touch-manipulation"
          style={{ minWidth: 48, minHeight: 48 }}
        >
          <Minimize2 className="h-6 w-6" />
        </button>
      </div>

      {/* Call Info - centered in upper area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 -mt-8">
        {/* Avatar */}
        <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mb-6 border border-white/20">
          <Phone className="h-10 w-10 text-white/80" />
        </div>

        {/* Name */}
        <h2
          className="text-white text-2xl font-semibold mb-1 text-center"
          style={{ letterSpacing: '-0.02em' }}
        >
          {displayName}
        </h2>

        {/* Phone number (show separately if we have a name) */}
        {activeCallState.customerInfo && (
          <p className="text-white/50 text-sm mb-3">
            {formatPhoneNumber(activeCallState.phoneNumber)}
          </p>
        )}

        {/* Status / Duration */}
        <p className={`text-lg font-medium ${
          activeCallState.callStatus === 'connected' ? 'text-green-400' : 'text-white/60'
        }`}>
          {callStatusText}
        </p>
      </div>

      {/* Keypad Overlay */}
      {showKeypad && (
        <div className="px-6 pb-4">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
            <div className="grid grid-cols-3 gap-3 justify-items-center mb-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((key) => (
                <button
                  key={key}
                  onClick={() => handleKeypadPress(key)}
                  className="h-14 w-14 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center text-2xl font-light text-white transition-all duration-150 active:scale-95 touch-manipulation border border-white/10"
                  type="button"
                >
                  {key}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowKeypad(false)}
              className="w-full py-2 text-sm font-medium text-white/50 hover:text-white/80 rounded-lg transition-colors touch-manipulation"
            >
              Hide Keypad
            </button>
          </div>
        </div>
      )}

      {/* Call Controls */}
      <div
        className="px-6 pb-4"
        style={{
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))',
        }}
      >
        <div className="grid grid-cols-3 gap-4 mb-8">
          {/* Mute */}
          <button
            onClick={toggleMute}
            className={`flex flex-col items-center py-4 rounded-2xl transition-all duration-200 active:scale-95 touch-manipulation ${
              activeCallState.isMuted
                ? 'bg-white text-gray-900'
                : 'bg-white/10 text-white border border-white/10'
            }`}
          >
            {activeCallState.isMuted ? (
              <MicOff className="h-7 w-7 mb-2" />
            ) : (
              <Mic className="h-7 w-7 mb-2" />
            )}
            <span className="text-xs font-medium">Mute</span>
          </button>

          {/* Keypad */}
          <button
            onClick={() => setShowKeypad(!showKeypad)}
            className={`flex flex-col items-center py-4 rounded-2xl transition-all duration-200 active:scale-95 touch-manipulation ${
              showKeypad
                ? 'bg-white text-gray-900'
                : 'bg-white/10 text-white border border-white/10'
            }`}
          >
            <Grid3x3 className="h-7 w-7 mb-2" />
            <span className="text-xs font-medium">Keypad</span>
          </button>

          {/* Speaker */}
          <button
            onClick={toggleSpeaker}
            className={`flex flex-col items-center py-4 rounded-2xl transition-all duration-200 active:scale-95 touch-manipulation ${
              activeCallState.isSpeakerOn
                ? 'bg-white text-gray-900'
                : 'bg-white/10 text-white border border-white/10'
            }`}
          >
            {activeCallState.isSpeakerOn ? (
              <Volume2 className="h-7 w-7 mb-2" />
            ) : (
              <VolumeX className="h-7 w-7 mb-2" />
            )}
            <span className="text-xs font-medium">Speaker</span>
          </button>
        </div>

        {/* End Call Button */}
        <div className="flex justify-center">
          <button
            onClick={endCall}
            className="w-20 h-20 rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 flex items-center justify-center transition-all duration-200 shadow-lg shadow-red-500/30 active:scale-95 touch-manipulation"
          >
            <Phone className="h-8 w-8 text-white rotate-[135deg]" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
